import { type ChangeEvent, useId, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  CoverCropper,
  type CoverCropperLabels,
} from "@/components/cover-cropper"
import { MasterclassSwatch } from "@/components/learn/masterclass-swatch"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import {
  MASTERCLASS_COVER_ASPECT_CLASS,
  MASTERCLASS_COVER_ASPECT_RATIO,
} from "@/lib/masterclass-cover"
import { cn } from "@/lib/utils"

/**
 * OKS-649 / OKS-768 — Masterclass cover image upload control inside the
 * shared `masterclass-form.tsx` (New + Edit). Banner adaptation of the
 * shipped `RewardCoverImageField` (OKS-438):
 *   - **Banner preview** locked to the masterclass banner ratio
 *     (`MASTERCLASS_COVER_ASPECT_CLASS`, 3:2) — the same ratio every
 *     display surface renders, so the framed image is pixel-identical
 *     everywhere (framed==shown).
 *   - **`MasterclassSwatch` fallback tile** (not a kind-icon) — the same
 *     deterministic swatch every display surface falls back to.
 *
 * In-form multipart: the hidden `<input type="file">` lives inside the
 * parent Inertia `<Form>` so the submit is auto-detected as multipart
 * (default Active Storage service — NOT direct-upload, sidesteps the
 * OKS-463 422 class). Removal stages a hidden
 * `masterclass[remove_cover_image]` flag posted on the single form Save
 * (no dedicated DELETE route).
 *
 * OKS-768 — every pick/replace of an accepted image now routes through
 * the shared `CoverCropper` (a 3:2 `react-easy-crop` step) BEFORE
 * anything is staged. The OS-picked original is cleared from the input on
 * pick; only the cropped 3:2 Blob is injected back (via `DataTransfer`)
 * and submitted, so no uncropped image ever reaches the server and the
 * framed area is what every surface shows (client-crop-authoritative). A
 * non-image pick bypasses the cropper and defers to server validation.
 *
 * Preview precedence (UX Spec §2): freshly cropped file (object URL) →
 * current resolved cover (`currentUrl`, unless removal is staged) → swatch
 * tile.
 *
 * Crew form rules: plain shadcn `Input`/`Label`/`Button`/`Dialog`; NO
 * shadcn Form / react-hook-form / zod. The crop widget is NOT a form.
 * Errors render from the parent `<Form>` render-prop `errors.cover_image`.
 */

/**
 * OKS-768 — Accepted cover MIME types (mirrors the server's
 * `Masterclass::COVER_IMAGE_CONTENT_TYPES`). Drives BOTH the file input's
 * `accept` filter AND the crop-vs-bypass decision on pick: a non-image
 * type can't be framed, so it skips the cropper and defers to server
 * validation (OKS-697 contract).
 */
export const COVER_ACCEPT_TYPES = ["image/jpeg", "image/png", "image/webp"]

const MASTERCLASS_COVER_CROP_LABELS: CoverCropperLabels = {
  title: "masterclasses.form.cover.crop_title",
  description: "masterclasses.form.cover.crop_description",
  zoom: "masterclasses.form.cover.crop_zoom",
  cancel: "masterclasses.form.cover.crop_cancel",
  apply: "masterclasses.form.cover.crop_apply",
  decodeError: "masterclasses.form.cover.crop_decode_error",
}

export interface MasterclassCoverImageFieldProps {
  /** Resolved current cover URL from Inertia props
   *  (`masterclass.cover_image_url` — attached `:card` variant). Null
   *  when no cover exists. */
  currentUrl: string | null
  /** True when an Active Storage attachment exists (gates the Remove
   *  affordance). From `masterclass.has_cover_image`. */
  hasCoverImage: boolean
  /** Masterclass id — drives the swatch fallback palette. Pass `null`
   *  on the unsaved New form (swatch renders its deterministic tone 0). */
  masterclassId: number | null
  /** Validation error from the parent `<Form>` render-prop
   *  (`errors.cover_image`). */
  error?: string | string[]
  /** Wire-format strong-params file input name. */
  inputName?: string
  /** Wire-format remove-flag input name. */
  removeName?: string
  testId?: string
}

export function MasterclassCoverImageField({
  currentUrl,
  hasCoverImage,
  masterclassId,
  error,
  inputName = "masterclass[cover_image]",
  removeName = "masterclass[remove_cover_image]",
  testId = "masterclass-cover-image-field",
}: MasterclassCoverImageFieldProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const inputId = useId()
  const helperId = `${inputId}-helper`
  const errorId = `${inputId}-error`

  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [removeStaged, setRemoveStaged] = useState(false)
  const [removeOpen, setRemoveOpen] = useState(false)
  // OKS-768 — crop step. A picked/replaced image opens the cropper; only the
  // cropped (3:2) Blob is ever staged into the hidden multipart input, so
  // the uncropped original is never submitted. `cropSrc` is the object URL of
  // the source handed to `react-easy-crop`; `cropMime` passes the source type
  // through to the canvas export.
  const [cropOpen, setCropOpen] = useState(false)
  const [cropSrc, setCropSrc] = useState<string | null>(null)
  const [cropMime, setCropMime] = useState<string | undefined>(undefined)
  // OKS-768 — Edit-load skeleton (UX Spec §3). Gates ONLY the
  // server-provided `currentUrl` branch: a freshly picked/cropped local
  // preview is an in-memory object URL with no meaningful network wait, so
  // it's never skeleton-gated. Resets whenever `currentUrl` itself changes
  // (different masterclass's edit form) so a stale "loaded" flag can't hide a
  // fresh skeleton opportunity — adjusted during render (React's documented
  // "resetting state when a prop changes" pattern) rather than in an effect.
  const [currentUrlLoaded, setCurrentUrlLoaded] = useState(false)
  const [trackedCurrentUrl, setTrackedCurrentUrl] = useState(currentUrl)
  if (currentUrl !== trackedCurrentUrl) {
    setTrackedCurrentUrl(currentUrl)
    setCurrentUrlLoaded(false)
  }

  const hasError = Boolean(error)

  const onPick = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    // Picking a file always wins over a staged removal.
    setRemoveStaged(false)

    // OKS-768 — a NON-image type can't be framed by the cropper. Bypass the
    // crop step and leave the raw file on the input so the server's
    // content-type validation produces the canonical inline error
    // (`errors.cover_image`) on submit. Only accepted image types route
    // through the mandatory crop.
    if (!COVER_ACCEPT_TYPES.includes(file.type)) {
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev)
        return URL.createObjectURL(file)
      })
      return
    }

    // Image: route every pick/replace through the crop step. The OS picker
    // populates the hidden input with the UNCROPPED original; clear it
    // immediately so a form submit before *Appliquer* can't send the raw
    // file. Only `onCropApply` re-populates the input (with the 3:2 Blob).
    if (inputRef.current) inputRef.current.value = ""
    setCropMime(file.type || undefined)
    setCropSrc(URL.createObjectURL(file))
    setCropOpen(true)
  }

  // OKS-768 — apply the cropped 3:2 Blob: wrap it in a File, inject it into
  // the hidden multipart input via `DataTransfer` (the only way to
  // programmatically set `input.files`), and update the preview.
  const onCropApply = (blob: Blob) => {
    const ext =
      blob.type === "image/png"
        ? "png"
        : blob.type === "image/webp"
          ? "webp"
          : "jpg"
    const file = new File([blob], `masterclass-cover.${ext}`, {
      type: blob.type || "image/jpeg",
    })
    if (inputRef.current) {
      const transfer = new DataTransfer()
      transfer.items.add(file)
      inputRef.current.files = transfer.files
    }
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return URL.createObjectURL(blob)
    })
    closeCropper()
  }

  const onCropCancel = () => {
    // No file staged — the input stays cleared, the prior preview/cover is
    // untouched (UX Spec §1 "Cancel crop").
    closeCropper()
  }

  const closeCropper = () => {
    setCropOpen(false)
    if (cropSrc) URL.revokeObjectURL(cropSrc)
    setCropSrc(null)
    setCropMime(undefined)
  }

  const onClear = () => {
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return null
    })
    if (inputRef.current) inputRef.current.value = ""
  }

  const onReplaceClick = () => {
    inputRef.current?.click()
  }

  const confirmRemove = () => {
    setRemoveStaged(true)
    onClear()
    setRemoveOpen(false)
  }

  const undoRemove = () => {
    setRemoveStaged(false)
  }

  // The banner shows: a freshly picked file > the current cover (unless
  // removal is staged) > the swatch fallback tile.
  const displayUrl = previewUrl ?? (removeStaged ? null : currentUrl)
  // OKS-768 — true only while `displayUrl` IS the server-provided
  // `currentUrl` (no local pick in front of it, not staged for removal) —
  // the only branch the edit-load skeleton applies to.
  const showingServerCover = !previewUrl && !removeStaged && Boolean(currentUrl)
  const describedBy =
    [hasError ? errorId : null, helperId].filter(Boolean).join(" ") || undefined

  return (
    <div data-testid={testId} className="grid gap-2">
      <Label htmlFor={inputId}>{t("masterclasses.form.cover.label")}</Label>

      <div className="flex flex-col gap-3">
        <div className="w-full max-w-md">
          {displayUrl ? (
            <>
              {/* OKS-768 — edit-load skeleton: shows only while the
                  server-provided `currentUrl` is loading for the first
                  time; the `<img>` stays mounted (so `onLoad` still fires)
                  but visually hidden underneath until it resolves. */}
              {showingServerCover && !currentUrlLoaded ? (
                <Skeleton
                  data-testid={`${testId}-loading-skeleton`}
                  className={cn(
                    MASTERCLASS_COVER_ASPECT_CLASS,
                    "w-full rounded-lg",
                  )}
                />
              ) : null}
              <img
                src={displayUrl}
                alt={t("masterclasses.form.cover.preview_alt")}
                data-testid={`${testId}-preview`}
                onLoad={() => {
                  if (showingServerCover) setCurrentUrlLoaded(true)
                }}
                className={cn(
                  MASTERCLASS_COVER_ASPECT_CLASS,
                  "w-full rounded-lg object-cover",
                  showingServerCover && !currentUrlLoaded && "hidden",
                )}
              />
            </>
          ) : (
            <MasterclassSwatch
              id={masterclassId ?? 0}
              size="card"
              className="rounded-lg"
              data-testid={`${testId}-fallback`}
            />
          )}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onReplaceClick}
            data-testid={`${testId}-upload`}
            className="min-h-11"
          >
            {hasCoverImage || previewUrl
              ? t("masterclasses.form.cover.replace")
              : t("masterclasses.form.cover.upload")}
          </Button>

          {previewUrl ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClear}
              data-testid={`${testId}-clear`}
              className="text-muted-foreground sm:self-center"
            >
              {t("masterclasses.form.cover.clear")}
            </Button>
          ) : null}

          {hasCoverImage && !previewUrl && !removeStaged ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setRemoveOpen(true)}
              data-testid={`${testId}-remove`}
              className="min-h-11 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            >
              {t("masterclasses.form.cover.remove")}
            </Button>
          ) : null}

          {removeStaged ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={undoRemove}
              data-testid={`${testId}-remove-undo`}
              className="text-muted-foreground sm:self-center"
            >
              {t("masterclasses.form.cover.remove_undo")}
            </Button>
          ) : null}
        </div>
      </div>

      {/* Hidden file input lives inside the parent Inertia <Form> so the
          submit is auto-detected as multipart. */}
      <Input
        ref={inputRef}
        id={inputId}
        type="file"
        name={inputName}
        accept={COVER_ACCEPT_TYPES.join(",")}
        onChange={onPick}
        className="sr-only"
        data-testid={`${testId}-input`}
        aria-invalid={hasError}
        aria-describedby={describedBy}
      />

      {/* Staged removal — only rendered (and thus posted) when the brand
          admin confirmed Remove. Absent otherwise, so a plain Save never
          purges. */}
      {removeStaged ? (
        <input
          type="hidden"
          name={removeName}
          value="1"
          data-testid={`${testId}-remove-flag`}
        />
      ) : null}

      <p id={helperId} className={cn("text-muted-foreground text-xs")}>
        {t("masterclasses.form.cover.helper")}
      </p>

      {hasError ? (
        <p id={errorId} role="alert" className="text-destructive text-sm">
          {Array.isArray(error) ? error[0] : error}
        </p>
      ) : null}

      <Dialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <DialogContent data-testid={`${testId}-remove-dialog`}>
          <DialogTitle>
            {t("masterclasses.form.cover.remove_confirm_title")}
          </DialogTitle>
          <DialogDescription>
            {t("masterclasses.form.cover.remove_confirm_body")}
          </DialogDescription>
          <DialogFooter>
            {/* Cancel is primary-focus — destructive action is never the
                default focus target (mirrors the Reward cover Dialog). */}
            <DialogClose
              render={
                <Button
                  variant="secondary"
                  autoFocus
                  data-testid={`${testId}-remove-cancel`}
                >
                  {t("masterclasses.form.cover.remove_confirm_cancel")}
                </Button>
              }
            />
            <Button
              variant="destructive"
              onClick={confirmRemove}
              data-testid={`${testId}-remove-confirm`}
            >
              {t("masterclasses.form.cover.remove_confirm_cta")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OKS-768 — crop-to-3:2 step for every accepted image pick/replace. */}
      <CoverCropper
        open={cropOpen}
        imageSrc={cropSrc}
        mimeType={cropMime}
        aspectRatio={MASTERCLASS_COVER_ASPECT_RATIO}
        aspectClass={MASTERCLASS_COVER_ASPECT_CLASS}
        labels={MASTERCLASS_COVER_CROP_LABELS}
        onApply={onCropApply}
        onCancel={onCropCancel}
        testId="masterclass-cover-crop"
      />
    </div>
  )
}

export default MasterclassCoverImageField
